import { 
  Box, 
  Button, 
  Flex, 
  FormControl, 
  FormLabel, 
  Input, 
  VStack, 
  Heading, 
  Text,
  Icon,
  Container,
  Image,
  keyframes,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { FaArrowLeft, FaLock, FaEnvelope, FaCheckCircle, FaGoogle } from "react-icons/fa";
import AnimatedBox from './AnimatedBox';
import LandingHeader from './LandingHeader';
import { getUserProfile } from '../services/userService';
import { logger } from '../utils/logger';

// A user is provisioned once they have a vertical, agency access, or an
// assigned business — otherwise they signed up but never entered the
// checkout/onboarding funnel.
const isProvisioned = (profile) =>
  Boolean(
    profile &&
    (profile.vertical ||
      profile.is_agency_owner ||
      (profile.assigned_businesses || []).length > 0)
  );

// Floating animation keyframes
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const floatReverse = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(20px) rotate(-5deg); }
`;

// Floating decoration component
const FloatingElement = ({ children, animation, delay, size, position, opacity = 0.6 }) => (
  <Box
    position="absolute"
    {...position}
    animation={`${animation} ${6 + delay}s ease-in-out infinite`}
    animationDelay={`${delay}s`}
    opacity={opacity}
    pointerEvents="none"
    fontSize={size}
    zIndex={0}
  >
    {children}
  </Box>
);

// Glassmorphism card component
const GlassCard = ({ children, ...props }) => (
  <Box
    bg="rgba(255, 255, 255, 0.95)"
    backdropFilter="blur(20px)"
    borderRadius="2xl"
    border="1px solid"
    borderColor="whiteAlpha.500"
    boxShadow="0 8px 32px rgba(0, 0, 0, 0.1)"
    {...props}
  >
    {children}
  </Box>
);

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkMessage, setMagicLinkMessage] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const bgColor = '#003d99';
    document.documentElement.style.backgroundColor = bgColor;
    document.body.style.backgroundColor = bgColor;
    return () => {
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
    };
  }, []);

  // Handle billing=success → send password reset email so user can set a password
  useEffect(() => {
    if (searchParams.get('billing') !== 'success') return;

    const emailFromUrl = searchParams.get('email');
    const emailForSignIn = emailFromUrl || localStorage.getItem('emailForSignIn');

    if (!emailForSignIn) return;

    const auth = getAuth();

    sendPasswordResetEmail(auth, emailForSignIn)
      .then(() => {
        localStorage.removeItem('emailForSignIn');
        setMagicLinkSent(true);
        setMagicLinkMessage(
          `Payment successful! We sent a link to ${emailForSignIn} to set your password. Check your email to get started.`
        );
      })
      .catch((err) => {
        console.error('Error sending password reset email:', err);
        setError('Payment received, but we could not send your setup email. Please use "Forgot Password" or contact support.');
      });
  }, [searchParams]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);
    const auth = getAuth();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/home');
    } catch (error) {
      console.error('Login error:', error.code, error.message);
      const errorMessages = {
        'auth/invalid-credential': 'Invalid email or password.',
        'auth/user-not-found': 'Invalid email or password.',
        'auth/wrong-password': 'Invalid email or password.',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-disabled': 'This account has been disabled. Please contact support.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
      };
      setError(errorMessages[error.code] || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    const auth = getAuth();
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      // New Google users bypass the checkout funnel entirely — send anyone
      // without provisioned access to the landing flow instead of an empty home.
      let destination = '/home';
      try {
        const profile = await getUserProfile();
        if (!isProvisioned(profile)) {
          destination = '/landing';
        }
      } catch (profileError) {
        logger.error('Post-login profile check failed:', profileError);
      }
      navigate(destination);
    } catch (error) {
      logger.error('Google sign-in error:', error.code, error.message);
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        return; // user dismissed the popup — not an error worth showing
      }
      const errorMessages = {
        'auth/popup-blocked': 'The sign-in popup was blocked. Please allow popups for this site and try again.',
        'auth/account-exists-with-different-credential': 'An account already exists with this email. Please sign in with your password.',
        'auth/user-disabled': 'This account has been disabled. Please contact support.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
      };
      setError(errorMessages[error.code] || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    if (!email) {
      setError('Please enter your email address above, then click "Send Reset Link".');
      return;
    }
    setResetLoading(true);
    const auth = getAuth();
    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
      setError('');
    } catch (err) {
      const errorMessages = {
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-not-found': 'No account found with that email address.',
        'auth/too-many-requests': 'Too many requests. Please try again later.',
      };
      setError(errorMessages[err.code] || 'Failed to send reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box 
      minH="100vh" 
      bg="linear-gradient(135deg, #0066ff 0%, #0052cc 50%, #003d99 100%)"
      position="relative"
      overflow="hidden"
    >
      {/* Floating Decorative Elements */}
      <FloatingElement animation={float} delay={0} size="80px" position={{ top: "10%", left: "5%" }} opacity={0.3}>
        <Box bg="white" borderRadius="full" w="80px" h="80px" />
      </FloatingElement>
      <FloatingElement animation={floatReverse} delay={1} size="60px" position={{ top: "20%", right: "10%" }} opacity={0.25}>
        <Box bg="white" borderRadius="xl" w="60px" h="60px" transform="rotate(45deg)" />
      </FloatingElement>
      <FloatingElement animation={float} delay={2} size="100px" position={{ bottom: "20%", left: "8%" }} opacity={0.2}>
        <Box bg="white" borderRadius="full" w="100px" h="100px" />
      </FloatingElement>
      <FloatingElement animation={floatReverse} delay={0.5} size="40px" position={{ bottom: "30%", right: "15%" }} opacity={0.3}>
        <Box bg="white" borderRadius="full" w="40px" h="40px" />
      </FloatingElement>
      <FloatingElement animation={float} delay={1.5} size="50px" position={{ top: "60%", left: "20%" }} opacity={0.15}>
        <Box bg="white" borderRadius="lg" w="50px" h="50px" />
      </FloatingElement>

      {/* Navigation */}
      <LandingHeader logoTo="/" />

      {/* Login Form */}
      <Flex 
        minH="calc(100vh - 72px)" 
        alignItems="center" 
        justifyContent="center"
        position="relative"
        zIndex={10}
        px={4}
      >
        <AnimatedBox animation="scale" delay={0.3}>
          <GlassCard p={{ base: 6, md: 10 }} w="full" maxW="440px">
            <VStack spacing={8}>
              {/* Magic link success banner */}
              {magicLinkSent && (
                <AnimatedBox animation="fadeIn" delay={0} w="full">
                  <Box
                    bg="green.50"
                    border="1px solid"
                    borderColor="green.200"
                    borderRadius="lg"
                    p={4}
                  >
                    <Flex align="center" gap={3}>
                      <Icon as={FaCheckCircle} color="green.500" boxSize={5} flexShrink={0} />
                      <Text color="green.700" fontSize="sm">
                        {magicLinkMessage}
                      </Text>
                    </Flex>
                  </Box>
                </AnimatedBox>
              )}

              {/* Header */}
              <VStack spacing={3} textAlign="center">
                <AnimatedBox animation="slideUp" delay={0.4}>
                  <Flex
                    w={16}
                    h={16}
                    bg="linear-gradient(135deg, #0066ff 0%, #0052cc 100%)"
                    borderRadius="2xl"
                    align="center"
                    justify="center"
                    boxShadow="0 4px 20px rgba(0, 102, 255, 0.4)"
                  >
                    <Icon as={FaLock} color="white" boxSize={7} />
                  </Flex>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.5}>
                  <Heading size="lg" color="gray.800">Welcome Back</Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.6}>
                  <Text color="gray.500" fontSize="sm">
                    Sign in to access your dashboard
                  </Text>
                </AnimatedBox>
              </VStack>

              {/* Form */}
              <form onSubmit={handleLogin} style={{ width: '100%' }}>
                <VStack spacing={5}>
                  <AnimatedBox animation="slideUp" delay={0.6} w="full">
                    <FormControl id="email" isRequired>
                      <FormLabel color="gray.600" fontSize="sm" fontWeight="medium">
                        Email Address
                      </FormLabel>
                      <Box position="relative">
                        <Box
                          position="absolute"
                          left={4}
                          top="50%"
                          transform="translateY(-50%)"
                          zIndex={1}
                        >
                          <Icon as={FaEnvelope} color="gray.400" boxSize={4} />
                        </Box>
                        <Input 
                          type="email" 
                          value={email} 
                          onChange={(e) => setEmail(e.target.value)}
                          size="lg"
                          pl={12}
                          bg="white"
                          borderColor="gray.200"
                          borderRadius="xl"
                          _hover={{ borderColor: "blue.300" }}
                          _focus={{ 
                            borderColor: "blue.500", 
                            boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" 
                          }}
                          placeholder="you@example.com"
                        />
                      </Box>
                    </FormControl>
                  </AnimatedBox>

                  <AnimatedBox animation="slideUp" delay={0.7} w="full">
                    <FormControl id="password" isRequired>
                      <FormLabel color="gray.600" fontSize="sm" fontWeight="medium">
                        Password
                      </FormLabel>
                      <Box position="relative">
                        <Box
                          position="absolute"
                          left={4}
                          top="50%"
                          transform="translateY(-50%)"
                          zIndex={1}
                        >
                          <Icon as={FaLock} color="gray.400" boxSize={4} />
                        </Box>
                        <Input 
                          type="password" 
                          value={password} 
                          onChange={(e) => setPassword(e.target.value)}
                          size="lg"
                          pl={12}
                          bg="white"
                          borderColor="gray.200"
                          borderRadius="xl"
                          _hover={{ borderColor: "blue.300" }}
                          _focus={{ 
                            borderColor: "blue.500", 
                            boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" 
                          }}
                          placeholder="Enter your password"
                        />
                      </Box>
                    </FormControl>
                  </AnimatedBox>

                  {error && (
                    <AnimatedBox animation="fadeIn" delay={0} w="full">
                      <Box 
                        bg="red.50" 
                        border="1px solid" 
                        borderColor="red.200" 
                        borderRadius="lg" 
                        p={3}
                      >
                        <Text color="red.600" fontSize="sm" textAlign="center">
                          {error}
                        </Text>
                      </Box>
                    </AnimatedBox>
                  )}

                  <AnimatedBox animation="fadeIn" delay={0.8} w="full">
                    <Button
                      type="submit"
                      w="full"
                      size="lg"
                      bg="linear-gradient(135deg, #0066ff 0%, #0052cc 100%)"
                      color="white"
                      borderRadius="xl"
                      isLoading={isLoading}
                      loadingText="Signing in..."
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 40px rgba(0, 102, 255, 0.4)"
                      }}
                      _active={{
                        transform: "translateY(0)",
                      }}
                      transition="all 0.2s"
                    >
                      Sign In
                    </Button>
                  </AnimatedBox>
                </VStack>
              </form>

              {/* Google Sign-In */}
              <AnimatedBox animation="fadeIn" delay={0.85} w="full">
                <VStack spacing={4} mt={-2}>
                  <Flex align="center" w="full" gap={3}>
                    <Box h="1px" flex={1} bg="gray.200" />
                    <Text mb={0} color="gray.400" fontSize="xs" fontWeight="medium" textTransform="uppercase">
                      or
                    </Text>
                    <Box h="1px" flex={1} bg="gray.200" />
                  </Flex>
                  <Button
                    w="full"
                    size="lg"
                    variant="outline"
                    bg="white"
                    color="gray.700"
                    borderColor="gray.200"
                    borderRadius="xl"
                    leftIcon={<Icon as={FaGoogle} color="#4285F4" />}
                    isLoading={googleLoading}
                    loadingText="Signing in..."
                    onClick={handleGoogleSignIn}
                    _hover={{
                      borderColor: "blue.300",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 20px rgba(0, 102, 255, 0.15)"
                    }}
                    _active={{ transform: "translateY(0)" }}
                    transition="all 0.2s"
                  >
                    Continue with Google
                  </Button>
                </VStack>
              </AnimatedBox>

              {/* Forgot Password */}
              {!resetEmailSent && !forgotPasswordMode && (
                <Button
                  variant="link"
                  color="blue.500"
                  fontSize="sm"
                  fontWeight="medium"
                  _hover={{ color: "blue.600" }}
                  onClick={() => setForgotPasswordMode(true)}
                  mt={-4}
                >
                  Forgot Password?
                </Button>
              )}

              {forgotPasswordMode && !resetEmailSent && (
                <AnimatedBox animation="fadeIn" delay={0} w="full">
                  <VStack spacing={3} mt={-4}>
                    <Text color="gray.500" fontSize="sm" textAlign="center">
                      Enter your email above and click the button below to receive a password reset link.
                    </Text>
                    <Button
                      w="full"
                      size="md"
                      variant="outline"
                      colorScheme="blue"
                      borderRadius="xl"
                      isLoading={resetLoading}
                      loadingText="Sending..."
                      onClick={handleForgotPassword}
                    >
                      Send Reset Link
                    </Button>
                    <Button
                      variant="link"
                      color="gray.500"
                      fontSize="sm"
                      onClick={() => { setForgotPasswordMode(false); setError(''); }}
                    >
                      Back to Sign In
                    </Button>
                  </VStack>
                </AnimatedBox>
              )}

              {resetEmailSent && (
                <AnimatedBox animation="fadeIn" delay={0} w="full">
                  <Box
                    bg="green.50"
                    border="1px solid"
                    borderColor="green.200"
                    borderRadius="lg"
                    p={4}
                    mt={-4}
                  >
                    <Flex align="center" gap={3}>
                      <Icon as={FaCheckCircle} color="green.500" boxSize={5} flexShrink={0} />
                      <Text color="green.700" fontSize="sm">
                        Check your email for a password reset link. It may take a minute to arrive.
                      </Text>
                    </Flex>
                  </Box>
                </AnimatedBox>
              )}

              {/* Footer */}
              <AnimatedBox animation="fadeIn" delay={0.9} w="full">
                <VStack spacing={4}>
                  <Box h="1px" w="full" bg="gray.200" />
                  <Text color="gray.500" fontSize="sm" textAlign="center">
                    Don't have an account?{" "}
                    <Button
                      as={RouterLink}
                      to="/landing"
                      variant="link"
                      color="blue.500"
                      fontSize="sm"
                      fontWeight="semibold"
                      _hover={{ color: "blue.600" }}
                    >
                      Request Beta Access
                    </Button>
                  </Text>
                </VStack>
              </AnimatedBox>
            </VStack>
          </GlassCard>
        </AnimatedBox>
      </Flex>
    </Box>
  );
}

export default Login;
